import React, { useRef, useEffect, useState } from 'react';
import CurrencyInput from './CurrencyInput';
import { useCurrency } from '../../contexts/CurrencyContext';

const CheckoutInfoModal = ({
  isOpen,
  onClose,
  onConfirm,
  netAmount,
  cashReceived,
  onCashReceivedChange,
  bankReceived,
  onBankReceivedChange,
  title = "Checkout Info",
  confirmButtonText = "Confirm",
  cancelButtonText = "Cancel",
  showBankReceived = true,
  autoFocus = true,
  disabled = false,
  checkoutType = "sale", // "sale", "exchange" or "sale-return"
  // sale-return only: the refund is paid out rather than received, so the mode
  // is reported back to the parent instead of being derived from amount inputs.
  onPaymentModeChange,
  initialPaymentMode = 'cash'
}) => {
  const { formatPrice } = useCurrency();
  const cashReceivedInputRef = useRef(null);
  const bankReceivedInputRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const confirmButtonRef = useRef(null);
  const isRefund = checkoutType === 'sale-return';
  const [paymentMode, setPaymentMode] = useState(isRefund ? initialPaymentMode : 'bank'); // 'cash' | 'bank' | 'split'
  const roundToTwo = (value) => Math.round(((Number(value) || 0) + Number.EPSILON) * 100) / 100;

  // Calculate cash returned and validation based on checkout type
  const totalReceived = (Number(cashReceived) || 0) + (Number(bankReceived) || 0);
  
  // Round net amount to the nearest whole number (standard rounding)
  const roundedNetAmount = Math.round(netAmount);
  
  let cashReturned, isPaymentValid;

  if (isRefund) {
    // The store pays the customer, so there is nothing to collect and nothing to
    // validate — only which way the money goes out.
    cashReturned = roundToTwo(Math.abs(roundedNetAmount));
    isPaymentValid = true;
  } else if (checkoutType === "exchange") {
    // For exchanges: if netAmount is negative, store pays customer
    if (roundedNetAmount < 0) {
      // Store needs to pay customer - cash returned should equal the exchange difference
      cashReturned = roundToTwo(Math.abs(roundedNetAmount));
      isPaymentValid = true; // No validation needed when store pays
    } else {
      // Customer needs to pay or even exchange
      cashReturned = roundToTwo(Math.max(totalReceived - roundedNetAmount, 0));
      isPaymentValid = roundedNetAmount <= 0 || totalReceived >= roundedNetAmount;
    }
  } else {
    // For sales: validate based on selected payment mode
    if (paymentMode === 'cash') {
      cashReturned = roundToTwo(Math.max((Number(cashReceived) || 0) - roundedNetAmount, 0));
      isPaymentValid = (Number(cashReceived) || 0) >= roundedNetAmount;
    } else if (paymentMode === 'bank') {
      cashReturned = 0; // bank overage not returned as cash here
      isPaymentValid = (Number(bankReceived) || 0) >= roundedNetAmount;
    } else {
      // split: allow overpayment, return change as cash returned
      cashReturned = roundToTwo(Math.max(totalReceived - roundedNetAmount, 0));
      isPaymentValid = totalReceived >= roundedNetAmount;
    }
  }


  // A refund has no amount inputs, so opening the modal just re-seeds the mode
  // from the parent and parks focus on Confirm for a straight Enter.
  useEffect(() => {
    if (!isOpen || !isRefund) return;
    setPaymentMode(initialPaymentMode);
    if (autoFocus) {
      setTimeout(() => confirmButtonRef.current?.focus(), 100);
    }
  }, [isOpen, isRefund, initialPaymentMode, autoFocus]);

  // Auto-populate payment fields and focus when modal opens
  useEffect(() => {
    if (isOpen && !isRefund) {
      // Auto-populate payment fields with net amount
      const roundedNetAmount = Math.round(netAmount);
      if (checkoutType === 'sale') {
        if (paymentMode === 'cash') {
          onCashReceivedChange?.(roundedNetAmount);
        } else if (paymentMode === 'bank') {
          onBankReceivedChange?.(roundedNetAmount);
        }
        // For split mode, do not auto-populate - let user enter amounts manually
      } else if (checkoutType === 'exchange') {
        // For exchanges, do not auto-populate - let user enter amounts manually
        // This allows for different cash and bank amounts
      }

      // Auto-focus appropriate input when modal opens
      if (autoFocus) {
        setTimeout(() => {
          if (checkoutType === 'sale') {
            if (paymentMode === 'cash' && cashReceivedInputRef.current) {
              cashReceivedInputRef.current.focus();
            } else if (paymentMode === 'bank' && bankReceivedInputRef.current) {
              bankReceivedInputRef.current.focus();
            } else if (paymentMode === 'split' && cashReceivedInputRef.current) {
              cashReceivedInputRef.current.focus();
            }
          } else if (checkoutType === 'exchange' && cashReceivedInputRef.current) {
            cashReceivedInputRef.current.focus();
          }
        }, 100);
      }
    }
  }, [isOpen, isRefund, autoFocus, netAmount, paymentMode, checkoutType, onCashReceivedChange, onBankReceivedChange]);

  // Keyboard shortcut handler
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!isOpen) return;

      // Handle ESC key to close modal
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      // Refunds offer cash or bank only — no split, so no F3.
      if (isRefund && !event.ctrlKey && !event.altKey && !event.metaKey) {
        if (event.key === 'F1') {
          event.preventDefault();
          handlePaymentModeChange('cash');
          return;
        }
        if (event.key === 'F2') {
          event.preventDefault();
          handlePaymentModeChange('bank');
          return;
        }
      }

      // Function keys to switch payment mode in sale checkout
      if (checkoutType === 'sale' && !event.ctrlKey && !event.altKey && !event.metaKey) {
        if (event.key === 'F1') {
          event.preventDefault();
          handlePaymentModeChange('cash');
          setTimeout(() => cashReceivedInputRef.current?.focus(), 0);
          return;
        }
        if (event.key === 'F2') {
          event.preventDefault();
          handlePaymentModeChange('bank');
          setTimeout(() => bankReceivedInputRef.current?.focus(), 0);
          return;
        }
        if (event.key === 'F3') {
          event.preventDefault();
          handlePaymentModeChange('split');
          // Default focus to cash on split
          setTimeout(() => cashReceivedInputRef.current?.focus(), 0);
          return;
        }
      }

      // Handle Enter to confirm (but not if Cancel button is focused)
      if (event.key === 'Enter' && !event.ctrlKey) {
        const activeElement = document.activeElement;
        const isCancelButton = activeElement === cancelButtonRef.current;
        
        if (!isCancelButton && isPaymentValid && !disabled) {
          event.preventDefault();
          onConfirm();
        }
        return;
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isPaymentValid, disabled, onClose, onConfirm, checkoutType, isRefund]);

  const handlePaymentModeChange = (mode) => {
    setPaymentMode(mode);
    if (isRefund) {
      // No amount inputs to reconcile — just report the choice upward.
      onPaymentModeChange?.(mode);
      return;
    }
    // Reset amounts for hidden inputs
    if (mode === 'cash') {
      onBankReceivedChange?.(0);
    } else if (mode === 'bank') {
      onCashReceivedChange?.(0);
    } else if (mode === 'split') {
      // Clear both fields when switching to split mode
      onCashReceivedChange?.(0);
      onBankReceivedChange?.(0);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
          <div className="space-y-4">
            {isRefund && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Refund Mode</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => handlePaymentModeChange('cash')}
                    className={`px-3 py-2 rounded-lg border text-base ${paymentMode === 'cash' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                    title="Press F1 to select Cash"
                  >
                    <span>Cash</span>
                    <span className={`ml-2 text-xs ${paymentMode === 'cash' ? 'text-blue-100' : 'text-gray-400'}`}>(F1)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePaymentModeChange('bank')}
                    className={`px-3 py-2 rounded-lg border text-base ${paymentMode === 'bank' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                    title="Press F2 to select Bank"
                  >
                    <span>Bank</span>
                    <span className={`ml-2 text-xs ${paymentMode === 'bank' ? 'text-blue-100' : 'text-gray-400'}`}>(F2)</span>
                  </button>
                </div>
              </div>
            )}
            {checkoutType === 'sale' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => handlePaymentModeChange('cash')}
                    className={`px-3 py-2 rounded-lg border text-base ${paymentMode === 'cash' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                    title="Press F1 to select Cash"
                  >
                    <span>Cash</span>
                    <span className={`ml-2 text-xs ${paymentMode === 'cash' ? 'text-blue-100' : 'text-gray-400'}`}>(F1)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePaymentModeChange('bank')}
                    className={`px-3 py-2 rounded-lg border text-base ${paymentMode === 'bank' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                    title="Press F2 to select Bank"
                  >
                    <span>Bank</span>
                    <span className={`ml-2 text-xs ${paymentMode === 'bank' ? 'text-blue-100' : 'text-gray-400'}`}>(F2)</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handlePaymentModeChange('split')}
                    className={`px-3 py-2 rounded-lg border text-base ${paymentMode === 'split' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                    title="Press F3 to select Split"
                  >
                    <span>Split</span>
                    <span className={`ml-2 text-xs ${paymentMode === 'split' ? 'text-blue-100' : 'text-gray-400'}`}>(F3)</span>
                  </button>
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {isRefund ? 'Refund Amount' : 'Net Amount'}
              </label>
              <input
                type="text"
                value={formatPrice(isRefund ? Math.abs(roundedNetAmount) : roundedNetAmount)}
                readOnly
                className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50"
              />
              {isRefund && (
                <p className="text-xs text-gray-500 mt-1">
                  {paymentMode === 'bank'
                    ? 'Paid by bank transfer — kept out of the cash drawer total.'
                    : 'Paid out of the cash drawer.'}
                </p>
              )}
            </div>
            {((checkoutType === 'sale' && (paymentMode === 'cash' || paymentMode === 'split')) || checkoutType === 'exchange') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cash Received
                </label>
                <CurrencyInput
                  ref={cashReceivedInputRef}
                  value={cashReceived}
                  onChange={onCashReceivedChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Amount received in cash"
                  min={0}
                  disabled={disabled || (checkoutType === "exchange" && netAmount < 0) || (checkoutType === 'sale' && paymentMode === 'bank')}
                />
              </div>
            )}
            {((checkoutType === 'sale' && (paymentMode === 'bank' || paymentMode === 'split')) || (checkoutType === 'exchange' && showBankReceived)) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bank Received
                </label>
                <CurrencyInput
                  ref={bankReceivedInputRef}
                  value={bankReceived}
                  onChange={onBankReceivedChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Amount received via bank/online"
                  min={0}
                  disabled={disabled || (checkoutType === "exchange" && netAmount < 0) || (checkoutType === 'sale' && paymentMode === 'cash')}
                />
              </div>
            )}
            {!isRefund && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cash Returned
                </label>
                <input
                  type="text"
                  value={formatPrice(cashReturned)}
                  readOnly
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-50"
                />
                <p className="text-xs text-gray-500 mt-1">Change to return to customer</p>
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-6">
            <button
              ref={cancelButtonRef}
              onClick={onClose}
              disabled={disabled}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {cancelButtonText}
            </button>
            <button
              ref={confirmButtonRef}
              onClick={onConfirm}
              disabled={!isPaymentValid || disabled}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Confirm (Enter)"
            >
              {confirmButtonText}
            </button>
          </div>
          {!isPaymentValid && (
            <div className="mt-3 text-sm text-red-600 text-center">
              {checkoutType === 'sale' && paymentMode === 'split'
                ? 'Cash + Bank must be at least the net amount'
                : paymentMode === 'cash'
                  ? 'Cash received must be at least the net amount'
                  : 'Bank received must be at least the net amount'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutInfoModal;
